import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { FiArrowLeft, FiPlus, FiTrash2, FiSave, FiX, FiEdit2, FiMessageSquare } from "react-icons/fi";
import { ENDPOINTS as LMS_ENDPOINTS } from "../Utils/Endpoints";
import { Models } from "../Utils/ModelsLMS";
import "../Styles/LmsExtra.css";
import "../Styles/QuizConfig.css";
import "../Styles/GhostOptions.css";
import ObservacionesModal from "../Modals/ObservacionesModal";
import AlertDialog from "../Components/Shared/AlertDialog";

const Editarexamen = () => {
    const { examenId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { idCurso } = location.state || {};

    // Estados principales
    const [examen, setExamen] = useState(null);
    const [preguntas, setPreguntas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isObservacionesModalOpen, setIsObservacionesModalOpen] = useState(false);

    // Modal de nueva pregunta
    const [modalNuevaPregunta, setModalNuevaPregunta] = useState(false);
    const [nuevaPregunta, setNuevaPregunta] = useState({
        descripcion: "",
        // tipo: 'opcionMultiple' | 'textoLibre' | 'verdaderoFalso'
        tipo: "opcionMultiple",
        opciones: [
            { llave: "A", descripcion: "", orden: 1 },
            { llave: "B", descripcion: "", orden: 2 },
        ],
        // llave ("A","B",...) de la opción correcta
        respuestaCorrecta: "",
        permiteMultiple: false,
        respuestasCorrectas: [],
        // campos para texto libre (se calculan a partir de la respuesta entre llaves)
        longitudMaxima: 255,
        palabraClave: "",
        // campo para verdadero/falso (true = Verdadero, false = Falso)
        respuestaVerdaderoFalso: null,
    });

    // Placeholders por si luego editas preguntas
    const [modalEditarPregunta, setModalEditarPregunta] = useState(false);
    const [preguntaEditando, setPreguntaEditando] = useState(null);

    // Estados de configuración
    const [preguntasAleatorias, setPreguntasAleatorias] = useState(0); // 0 = todas
    const [limiteIntentos, setLimiteIntentos] = useState(3);

    // Edición inline de pregunta (doble click)
    const [editingQuestion, setEditingQuestion] = useState(null); // { idPregunta }
    const [questionTextDraft, setQuestionTextDraft] = useState("");

    // Modo de edición del modal (crear o editar)
    const [modoModal, setModoModal] = useState("crear"); // "crear" o "editar"
    const [preguntaEditandoId, setPreguntaEditandoId] = useState(null);
    const [validationError, setValidationError] = useState("");

    // Modal de alertas estándar LMS
    const [alertDialog, setAlertDialog] = useState({ open: false, title: "", message: "", variant: "info" });

    // ----------- Cargar examen y preguntas -----------
    useEffect(() => {
        loadExamen();
    }, [examenId]);

    // Inicializar preguntasAleatorias cuando se carga el examen
    useEffect(() => {
        if (examen && examen.preguntasAleatorias !== undefined) {
            setPreguntasAleatorias(examen.preguntasAleatorias);
        }
        if (examen && examen.limiteIntentos !== undefined) {
            setLimiteIntentos(examen.limiteIntentos);
        }
    }, [examen]);

    const loadExamen = async (showLoading = true) => {
        if (showLoading) {
            setLoading(true);
        }
        try {
            const idExamenInt = parseInt(examenId, 10);

            // 1) Examen (Optimizado)
            const resExamen = await axios.get(LMS_ENDPOINTS.examenById(idExamenInt));
            const examenData = Array.isArray(resExamen.data) ? resExamen.data[0] : resExamen.data;
            // Models.examen devuelve el objeto mapeado si existe, o undefined si falla algo.
            // Aseguramos que examenData exista.
            const examenEncontrado = examenData ? Models.examen(examenData) : null;

            setExamen(examenEncontrado || null);
            console.log("Examen cargado:", examenEncontrado);

            if (!examenEncontrado) {
                setPreguntas([]);
                if (showLoading) {
                    setLoading(false);
                }
                return;
            }

            // 2) Preguntas del Examen (Optimizado)
            const resPreguntas = await axios.get(LMS_ENDPOINTS.examenPreguntasByExamen(idExamenInt));
            const preguntasDelExamen = (resPreguntas.data || []).map(Models.examenPreguntas);

            // 3) Respuestas Texto Libre y VF (Filtrado por Examen)
            let todasTextoLibre = [];
            try {
                const resTL = await axios.get(LMS_ENDPOINTS.examenPreguntaTextoLibreByExamen(idExamenInt));
                todasTextoLibre = (resTL.data || []).map(Models.examenPreguntaTextoLibre);
            } catch (err) {
                console.warn("No se cargaron respuestas Texto Libre (posiblemente vacio):", err);
            }

            let todasVerdaderoFalso = [];
            try {
                const resVF = await axios.get(LMS_ENDPOINTS.examenPreguntaVerdaderoFalsoByExamen(idExamenInt));
                todasVerdaderoFalso = (resVF.data || []).map(Models.examenPreguntaVerdaderoFalso);
            } catch (err) {
                console.warn("No se cargaron respuestas VF (posiblemente vacio):", err);
            }

            // 4) Procesar cada pregunta y cargar Opciones Múltiples bajo demanda
            const preguntasConOpciones = await Promise.all(
                preguntasDelExamen.map(async (pregunta) => {
                    const idPregunta = pregunta.idExamenPregunta;

                    // Texto Libre
                    if (pregunta.idTipoPregunta === 1) {
                        const respTL = todasTextoLibre.find((r) => r.idExamenPregunta === idPregunta) || null;
                        return {
                            ...pregunta,
                            tipo: "textoLibre",
                            respuestaTextoLibre: respTL,
                            opciones: [],
                            idRespuesta: null
                        };
                    }

                    // VF
                    if (pregunta.idTipoPregunta === 3) {
                        const respVF = todasVerdaderoFalso.find((r) => r.idExamenPregunta === idPregunta) || null;
                        return {
                            ...pregunta,
                            tipo: "verdaderoFalso",
                            respuestaVerdaderoFalso: respVF,
                            opciones: [],
                            idRespuesta: null
                        };
                    }

                    // Opcion Multiple
                    try {
                        const resRespuesta = await axios.get(LMS_ENDPOINTS.examenPreguntaOpcionMultipleByPregunta(idPregunta));
                        const cabecera = Array.isArray(resRespuesta.data) ? resRespuesta.data[0] : resRespuesta.data;

                        if (cabecera) {
                            const idRespuestaOM = cabecera.idExamenRespuestaOpcionMultiple || cabecera.IdExamenRespuestaOpcionMultiple;
                            const resOpciones = await axios.get(LMS_ENDPOINTS.examenPreguntaOpcionMultipleOpcionesByRespuesta(idRespuestaOM));
                            const todasOpcionesOM = resOpciones.data || [];
                            const opcionesOrdenadas = todasOpcionesOM.sort((a, b) => (a.orden || a.Orden || 0) - (b.orden || b.Orden || 0));

                            const opciones = opcionesOrdenadas.map((o, idx) => ({
                                ...o,
                                idExamenOpcion: o.idExamenOpcion || o.IdExamenOpcion || o.idOpcion || o.IdOpcion,
                                llave: String.fromCharCode(65 + idx),
                                descripcion: o.texto || o.Texto || "",
                                esCorrecta: o.esCorrecta !== undefined ? o.esCorrecta : (o.EsCorrecta !== undefined ? o.EsCorrecta : false),
                                orden: o.orden || o.Orden || idx + 1,
                            }));

                            return {
                                ...pregunta,
                                tipo: "opcionMultiple",
                                opciones,
                                idRespuesta: idRespuestaOM,
                                permiteMultiple: cabecera.permiteMultiple || cabecera.PermiteMultiple || false,
                            };
                        }
                    } catch (err) { }

                    return { ...pregunta, tipo: "opcionMultiple", opciones: [], idRespuesta: null };
                })
            );

            setPreguntas(preguntasConOpciones);


        } catch (error) {
            console.error("Error al cargar examen:", error);
            setAlertDialog({
                open: true,
                title: "Error",
                message: "Error al cargar el examen. Inténtalo de nuevo más tarde.",
                variant: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    // ----------- Agregar nueva pregunta (Opción múltiple / texto libre) -----------
    const handleAgregarPregunta = async () => {
        if (!nuevaPregunta.descripcion.trim()) {
            setAlertDialog({
                open: true,
                title: "Validación",
                message: "Por favor ingresa la descripción de la pregunta.",
                variant: "warning",
            });
            return;
        }

        // Validaciones específicas por tipo
        if (nuevaPregunta.tipo === "opcionMultiple") {
            const opcionesValidas = nuevaPregunta.opciones.filter((o) =>
                o.descripcion.trim()
            );
            if (opcionesValidas.length < 2) {
                setAlertDialog({
                    open: true,
                    title: "Validación",
                    message: "Debes agregar al menos 2 opciones.",
                    variant: "warning",
                });
                return;
            }

            if (nuevaPregunta.permiteMultiple) {
                if (!nuevaPregunta.respuestasCorrectas || nuevaPregunta.respuestasCorrectas.length === 0) {
                    setAlertDialog({
                        open: true,
                        title: "Validación",
                        message: "Por favor selecciona al menos una respuesta correcta.",
                        variant: "warning",
                    });
                    return;
                }
            } else {
                const seleccion = nuevaPregunta.respuestaCorrecta;
                if (!seleccion) {
                    setAlertDialog({
                        open: true,
                        title: "Validación",
                        message: "Por favor selecciona la respuesta correcta.",
                        variant: "warning",
                    });
                    return;
                }
                const countSeleccionadas = opcionesValidas.filter(o => o.llave === seleccion).length;
                if (countSeleccionadas !== 1) {
                    setAlertDialog({
                        open: true,
                        title: "Validación",
                        message: "Debe existir exactamente una opción marcada como respuesta correcta.",
                        variant: "warning",
                    });
                    return;
                }
            }

        } else if (nuevaPregunta.tipo === "textoLibre") {
            const openBraces = (nuevaPregunta.descripcion.match(/\{/g) || []).length;
            const closeBraces = (nuevaPregunta.descripcion.match(/\}/g) || []).length;

            if (openBraces !== 1 || closeBraces !== 1) {
                setAlertDialog({
                    open: true,
                    title: "Validación",
                    message: "Formato inválido: Solo se permite un único par de llaves { } para definir la respuesta. Evita llaves dobles {{ }} o múltiples pares.",
                    variant: "warning",
                });
                return;
            }

            const matches = nuevaPregunta.descripcion.match(/\{[^}]*\}/g) || [];
            if (matches.length !== 1) {
                setAlertDialog({
                    open: true,
                    title: "Validación",
                    message: "En preguntas abiertas, coloca una sola respuesta esperada entre llaves, por ejemplo: La capital de Francia es {Paris}.",
                    variant: "warning",
                });
                return;
            }

            const inner = matches[0].slice(1, -1).trim();
            if (!inner || /[{}]/.test(inner)) {
                setAlertDialog({
                    open: true,
                    title: "Validación",
                    message: "La respuesta entre llaves no puede estar vacía ni contener más llaves.",
                    variant: "warning",
                });
                return;
            }
        } else if (nuevaPregunta.tipo === "verdaderoFalso") {
            if (nuevaPregunta.respuestaVerdaderoFalso === null) {
                setAlertDialog({
                    open: true,
                    title: "Validación",
                    message: "Por favor selecciona si la respuesta es Verdadero o Falso.",
                    variant: "warning",
                });
                return;
            }
        }

        const idExamenInt = parseInt(examenId, 10);
        const idTipoPregunta = nuevaPregunta.tipo === "textoLibre" ? 1 : nuevaPregunta.tipo === "verdaderoFalso" ? 3 : 2;
        let descripcionParaGuardar = nuevaPregunta.descripcion;
        let respuestaTextoLibreValor = "";

        if (idTipoPregunta === 1) {
            const matches = nuevaPregunta.descripcion.match(/\{[^}]*\}/g) || [];
            if (matches.length === 1) {
                const inner = matches[0].slice(1, -1).trim();
                if (inner && !/[{}]/.test(inner)) {
                    respuestaTextoLibreValor = inner;
                    descripcionParaGuardar = nuevaPregunta.descripcion.replace(/\{[^}]*\}/, "{}");
                }
            }
        }

        // 🚀 OPTIMISTIC UPDATE: Agregar pregunta inmediatamente a la UI
        const tempId = Date.now();
        const nuevaPreguntaOptimista = {
            idExamenPregunta: tempId,
            idExamen: idExamenInt,
            descripcionPregunta: descripcionParaGuardar,
            idTipoPregunta,
            tipo: nuevaPregunta.tipo,
            opciones: idTipoPregunta === 2 ? nuevaPregunta.opciones.filter(o => o.descripcion.trim()).map(o => ({
                ...o,
                idExamenOpcion: Date.now() + Math.random(),
                esCorrecta: o.llave === nuevaPregunta.respuestaCorrecta,
                texto: o.descripcion,
            })) : [],
            respuestaTextoLibre: idTipoPregunta === 1 ? {
                palabrasClave: respuestaTextoLibreValor,
                longitudMaxima: respuestaTextoLibreValor.length + 10
            } : null,
            idRespuesta: idTipoPregunta === 2 ? Date.now() + 1 : null,
        };

        setPreguntas(prev => [...prev, nuevaPreguntaOptimista]);
        setModalNuevaPregunta(false);
        const preguntaLimpia = {
            descripcion: "",
            tipo: "opcionMultiple",
            opciones: [
                { llave: "A", descripcion: "", orden: 1 },
                { llave: "B", descripcion: "", orden: 2 },
            ],
            respuestaCorrecta: "",
            longitudMaxima: 255,
            palabraClave: "",
        };
        setNuevaPregunta(preguntaLimpia);

        // 🔄 Sincronizar con el servidor en segundo plano
        try {

            const preguntaDTO = {
                IdExamenPregunta: 0,
                IdExamen: idExamenInt,
                DescripcionPregunta: descripcionParaGuardar,
                IdTipoPregunta: idTipoPregunta,
                IdRespuestaCorrecta: 0,
                Activo: true,
            };

            const resPregunta = await axios.post(
                LMS_ENDPOINTS.examenPreguntas,
                preguntaDTO
            );

            console.log("📥 Respuesta crear pregunta:", resPregunta);

            let idPregunta = null;
            if (typeof resPregunta.data === 'number') {
                idPregunta = resPregunta.data;
            } else if (typeof resPregunta.data === 'object' && resPregunta.data !== null) {
                idPregunta = resPregunta.data.idExamenPregunta || resPregunta.data.IdExamenPregunta;
            }
            idPregunta = parseInt(idPregunta, 10);

            if (!idPregunta || isNaN(idPregunta) || idPregunta <= 0) {
                console.error(
                    "❌ No se pudo obtener el id válido de la pregunta creada",
                    resPregunta.data
                );
                setAlertDialog({
                    open: true,
                    title: "Error",
                    message: "Error: el servidor no regresó un ID válido para la nueva pregunta.",
                    variant: "error",
                });
                return;
            }

            console.log("✅ ID Pregunta obtenido:", idPregunta);

            if (idTipoPregunta === 2) {
                // 2) Insertar cabecera en ExamenPreguntaOpcionMultiple
                const opcionesValidas = nuevaPregunta.opciones.filter((o) =>
                    o.descripcion.trim()
                );

                const respuestaDTO = {
                    IdExamenRespuestaOpcionMultiple: 0,
                    IdExamenPregunta: idPregunta,
                    PermiteMultiple: nuevaPregunta.permiteMultiple,
                    Activo: true,
                };

                const resRespuesta = await axios.post(
                    LMS_ENDPOINTS.examenPreguntaOpcionMultiple,
                    respuestaDTO
                );

                const idRespuesta =
                    resRespuesta.data.idExamenRespuestaOpcionMultiple ||
                    resRespuesta.data.IdExamenRespuestaOpcionMultiple ||
                    resRespuesta.data;

                if (!idRespuesta) {
                    console.error(
                        "❌ No se pudo obtener el id de la respuesta múltiple creada",
                        resRespuesta.data
                    );
                    setAlertDialog({
                        open: true,
                        title: "Error",
                        message: "Error: el servidor no regresó el ID de la respuesta de opción múltiple.",
                        variant: "error",
                    });
                    return;
                }

                // 3) Insertar opciones en ExamenPreguntaOpcionMultiple_Opcion
                let idOpcionCorrecta = null;

                for (const opcion of opcionesValidas) {
                    const esCorrecta = nuevaPregunta.permiteMultiple
                        ? (nuevaPregunta.respuestasCorrectas || []).includes(opcion.llave)
                        : opcion.llave === nuevaPregunta.respuestaCorrecta;

                    const opcionDTO = {
                        IdExamenOpcion: 0,
                        IdExamenRespuestaOpcionMultiple: idRespuesta,
                        Texto: opcion.descripcion,
                        EsCorrecta: esCorrecta,
                        Orden: opcion.orden,
                        Activo: true,
                    };

                    const resOpcion = await axios.post(
                        LMS_ENDPOINTS.examenPreguntaOpcionMultipleOpciones,
                        opcionDTO
                    );

                    const idOpcion =
                        resOpcion.data.idExamenOpcion ||
                        resOpcion.data.IdExamenOpcion ||
                        resOpcion.data;

                    if (esCorrecta) {
                        idOpcionCorrecta = idOpcion;
                    }
                }

                // 4) Actualizar IdRespuestaCorrecta en ExamenPreguntas (con la opción correcta)
                if (idOpcionCorrecta != null) {
                    const preguntaUpdateDTO = {
                        IdExamenPregunta: idPregunta,
                        IdExamen: idExamenInt,
                        DescripcionPregunta: descripcionParaGuardar,
                        IdTipoPregunta: 2,
                        IdRespuestaCorrecta: idOpcionCorrecta,
                        Activo: true,
                    };

                    await axios.put(
                        LMS_ENDPOINTS.examenPreguntas,
                        preguntaUpdateDTO
                    );
                }
            } else if (idTipoPregunta === 1) {
                // Pregunta de texto libre: insertar en ExamenPreguntaTextoLibre
                const respuesta = respuestaTextoLibreValor.trim();
                const longitud = respuesta.length + 10;

                const textoLibreDTO = {
                    IdExamenRespuestaTextoLibre: 0,
                    IdExamenPregunta: idPregunta,
                    LongitudMaxima: longitud,
                    PalabrasClave: respuesta,
                    Activo: true,
                };

                await axios.post(
                    LMS_ENDPOINTS.examenPreguntaTextoLibre,
                    textoLibreDTO
                );
            } else if (idTipoPregunta === 3) {
                // Pregunta de verdadero/falso: insertar en ExamenPreguntaVerdaderoFalso
                const verdaderoFalsoDTO = {
                    IdExamenRespuestaVF: 0,
                    IdExamenPregunta: idPregunta,
                    RespuestaCorrecta: nuevaPregunta.respuestaVerdaderoFalso,
                    Activo: true,
                };

                await axios.post(
                    LMS_ENDPOINTS.examenPreguntaVerdaderoFalso,
                    verdaderoFalsoDTO
                );
            }

            console.log("✅ Pregunta sincronizada con el servidor");
            await loadExamen(false);
        } catch (error) {
            console.error("❌ Error al crear pregunta:", error);
            setAlertDialog({
                open: true,
                title: "Error",
                message: "Error al crear la pregunta. Se revertirán los cambios.",
                variant: "error",
            });
            setPreguntas(prev => prev.filter(p => p.idExamenPregunta !== tempId));
            setNuevaPregunta({
                descripcion: descripcionParaGuardar,
                tipo: nuevaPregunta.tipo,
                opciones: nuevaPreguntaOptimista.opciones.length > 0 ? nuevaPreguntaOptimista.opciones : preguntaLimpia.opciones,
                respuestaCorrecta: nuevaPregunta.respuestaCorrecta || "",
                longitudMaxima: 255,
                palabraClave: respuestaTextoLibreValor,
            });
            setModalNuevaPregunta(true);
        }
    };

    // ----------- Eliminar pregunta completa -----------
    const handleEliminarPregunta = async (pregunta) => {
        setAlertDialog({
            open: true,
            title: "¿Estás seguro?",
            message: "¿Estás seguro de eliminar esta pregunta? Esta acción no se puede deshacer.",
            variant: "warning",
            onConfirm: async () => {
                const idPregunta = pregunta.idExamenPregunta;
                const preguntaEliminada = pregunta;

                // 🚀 OPTIMISTIC UPDATE: Eliminar inmediatamente de la UI
                setPreguntas(prev => prev.filter(p => p.idExamenPregunta !== idPregunta));

                // 🔄 Sincronizar con el servidor en segundo plano
                try {
                    // Eliminar opciones primero
                    if (pregunta.opciones && pregunta.opciones.length > 0) {
                        for (const opcion of pregunta.opciones) {
                            const idOpcion = opcion.idExamenOpcion || opcion.idOpcion || opcion.IdExamenOpcion;
                            if (!idOpcion) continue;

                            await axios.delete(
                                LMS_ENDPOINTS.examenPreguntaOpcionMultipleOpcionesById(
                                    idOpcion
                                )
                            );
                        }
                    }

                    // Eliminar cabecera de opción múltiple
                    if (pregunta.idRespuesta) {
                        await axios.delete(
                            LMS_ENDPOINTS.examenPreguntaOpcionMultipleById(
                                pregunta.idRespuesta
                            )
                        );
                    }

                    // Eliminar pregunta
                    await axios.delete(
                        LMS_ENDPOINTS.examenPreguntasById(idPregunta)
                    );

                    console.log("✅ Pregunta eliminada y sincronizada");
                    await loadExamen(false);
                } catch (error) {
                    console.error("❌ Error al eliminar pregunta:", error);
                    setAlertDialog({
                        open: true,
                        title: "Error",
                        message: "Error al eliminar la pregunta. Se revertirán los cambios.",
                        variant: "error",
                    });
                    setPreguntas(prev => [...prev, preguntaEliminada].sort((a, b) => a.idExamenPregunta - b.idExamenPregunta));
                }
            },
            onCancel: () => {
                // No hacer nada si se cancela
            }
        });
    };

    // ----------- Cambiar respuesta correcta -----------
    const handleCambiarRespuestaCorrecta = async (pregunta, opcionSeleccionada) => {
        // En modo simple, si ya es la correcta, no hacer nada. En modo múltiple, permitimos desmarcar.
        if (!pregunta.permiteMultiple && opcionSeleccionada.esCorrecta) return;

        console.log("🔄 Cambiando respuesta correcta...");

        // 🚀 OPTIMISTIC UPDATE: Actualizar UI inmediatamente
        const preguntaAnterior = { ...pregunta };

        // Calcular nuevas opciones con sus estados finales
        const nuevasOpciones = pregunta.opciones.map(o => {
            const esLaSeleccionada = (o.idExamenOpcion || o.idOpcion || o.IdExamenOpcion) ===
                (opcionSeleccionada.idExamenOpcion || opcionSeleccionada.idOpcion || opcionSeleccionada.IdExamenOpcion);
            let nuevaEsCorrecta = o.esCorrecta;

            if (pregunta.permiteMultiple) {
                // Toggle si es la seleccionada, mantener las demás
                if (esLaSeleccionada) {
                    nuevaEsCorrecta = !o.esCorrecta;
                }
            } else {
                // Solo la seleccionada es correcta
                nuevaEsCorrecta = esLaSeleccionada;
            }

            return {
                ...o,
                esCorrecta: nuevaEsCorrecta
            };
        });

        setPreguntas(prev => prev.map(p => {
            if (p.idExamenPregunta === pregunta.idExamenPregunta) {
                return {
                    ...p,
                    opciones: nuevasOpciones
                };
            }
            return p;
        }));

        // 🔄 Sincronizar con el servidor en segundo plano
        try {
            for (const opcion of nuevasOpciones) {
                const idOpcion = opcion.idExamenOpcion || opcion.idOpcion || opcion.IdExamenOpcion;
                if (!idOpcion) continue;

                // Optimización: verificar si cambió respecto al estado original
                const opcionOriginal = pregunta.opciones.find(opt => (opt.idExamenOpcion || opt.idOpcion || opt.IdExamenOpcion) === idOpcion);
                if (opcionOriginal && opcionOriginal.esCorrecta === opcion.esCorrecta) continue;

                const opcionDTO = {
                    IdExamenOpcion: idOpcion,
                    IdExamenRespuestaOpcionMultiple: pregunta.idRespuesta,
                    Texto: opcion.texto || opcion.Texto || opcion.descripcion || "",
                    EsCorrecta: opcion.esCorrecta,
                    Orden: opcion.orden || opcion.Orden || 0,
                    Activo: true,
                };

                await axios.put(
                    LMS_ENDPOINTS.examenPreguntaOpcionMultipleOpciones,
                    opcionDTO
                );
            }

            console.log("✅ Respuesta correcta actualizada y sincronizada");
            await loadExamen(false);
        } catch (error) {
            console.error("❌ Error al cambiar respuesta correcta:", error);
            setAlertDialog({
                open: true,
                title: "Error",
                message: "Error al cambiar la respuesta correcta. Se revertirán los cambios.",
                variant: "error",
            });
            setPreguntas(prev => prev.map(p => p.idExamenPregunta === pregunta.idExamenPregunta ? preguntaAnterior : p));
        }
    };

    // ----------- Cambiar respuesta verdadero/falso -----------
    const handleCambiarRespuestaVerdaderoFalso = async (pregunta, nuevoValor) => {
        if (pregunta.respuestaVerdaderoFalso?.respuestaCorrecta === nuevoValor) {
            return;
        }

        console.log("🔄 Cambiando respuesta verdadero/falso...");

        const preguntaAnterior = { ...pregunta };
        setPreguntas(prev => prev.map(p => {
            if (p.idExamenPregunta === pregunta.idExamenPregunta) {
                return {
                    ...p,
                    respuestaVerdaderoFalso: {
                        ...p.respuestaVerdaderoFalso,
                        respuestaCorrecta: nuevoValor,
                        RespuestaCorrecta: nuevoValor,
                    }
                };
            }
            return p;
        }));

        try {
            const respVF = pregunta.respuestaVerdaderoFalso || {};
            const idRespuestaVF = respVF.idExamenRespuestaVF || respVF.IdExamenRespuestaVF;

            if (idRespuestaVF) {
                const dto = {
                    IdExamenRespuestaVF: idRespuestaVF,
                    IdExamenPregunta: pregunta.idExamenPregunta,
                    RespuestaCorrecta: nuevoValor,
                    Activo: true,
                };

                await axios.put(
                    LMS_ENDPOINTS.examenPreguntaVerdaderoFalso,
                    dto
                );
            }

            console.log("✅ Respuesta verdadero/falso actualizada");
            await loadExamen(false);
        } catch (error) {
            console.error("❌ Error al cambiar respuesta verdadero/falso:", error);
            setAlertDialog({
                open: true,
                title: "Error",
                message: "Error al cambiar la respuesta. Se revertirán los cambios.",
                variant: "error",
            });
            setPreguntas(prev => prev.map(p => p.idExamenPregunta === pregunta.idExamenPregunta ? preguntaAnterior : p));
        }
    };

    // ----------- Agregar opción a la nueva pregunta -----------
    const handleAgregarOpcion = () => {
        if (nuevaPregunta.opciones.length >= 6) {
            setAlertDialog({
                open: true,
                title: "Validación",
                message: "Máximo 6 opciones permitidas.",
                variant: "warning",
            });
            return;
        }

        const letras = ["A", "B", "C", "D", "E", "F"];
        const nuevaLetra = letras[nuevaPregunta.opciones.length];

        setNuevaPregunta({
            ...nuevaPregunta,
            opciones: [
                ...nuevaPregunta.opciones,
                {
                    llave: nuevaLetra,
                    descripcion: "",
                    orden: nuevaPregunta.opciones.length + 1,
                },
            ],
        });
    };

    // ----------- Eliminar opción del modal -----------
    const handleEliminarOpcion = (index) => {
        if (nuevaPregunta.opciones.length <= 2) {
            setAlertDialog({
                open: true,
                title: "Validación",
                message: "Debes mantener al menos 2 opciones.",
                variant: "warning",
            });
            return;
        }

        const nuevasOpciones = nuevaPregunta.opciones.filter((_, i) => i !== index);

        // Re-asignar letras
        const opcionesActualizadas = nuevasOpciones.map((o, idx) => ({
            ...o,
            llave: String.fromCharCode(65 + idx),
        }));

        // Si la opción eliminada era la correcta, limpiar la selección
        const opcionEliminada = nuevaPregunta.opciones[index];
        const nuevaRespuestaCorrecta =
            nuevaPregunta.respuestaCorrecta === opcionEliminada.llave
                ? ""
                : nuevaPregunta.respuestaCorrecta;

        setNuevaPregunta({
            ...nuevaPregunta,
            opciones: opcionesActualizadas,
            respuestaCorrecta: nuevaRespuestaCorrecta,
        });
    };

    // ----------- Abrir modal para nueva pregunta -----------
    const handleAbrirModalNuevaPregunta = () => {
        setNuevaPregunta({
            descripcion: "",
            tipo: "opcionMultiple",
            opciones: [
                { llave: "A", descripcion: "", orden: 1 },
                { llave: "B", descripcion: "", orden: 2 },
                { llave: "C", descripcion: "", orden: 3 }, // Ghost inicial
            ],
            respuestaCorrecta: "",
            respuestasCorrectas: [],
            permiteMultiple: false,
            longitudMaxima: 255,
            palabraClave: "",
            respuestaVerdaderoFalso: null,
        });
        setModoModal("crear");
        setPreguntaEditandoId(null);
        setValidationError("");
        setModalNuevaPregunta(true);
    };

    // ----------- Edición de pregunta (modal) -----------
    const handleEditarPregunta = (pregunta) => {
        const idPregunta = pregunta.idExamenPregunta;
        const descripcion =
            pregunta.descripcionPregunta ||
            pregunta.DescripcionPregunta ||
            "";

        const idTipoPregunta = pregunta.idTipoPregunta || pregunta.IdTipoPregunta || 0;

        if (idTipoPregunta === 1) {
            // Pregunta de texto libre
            const respTL = pregunta.respuestaTextoLibre || {};

            // Reconstruimos la descripción mostrando la respuesta dentro de {}
            let descripcionConRespuesta = descripcion || "";
            if (respTL.palabrasClave) {
                descripcionConRespuesta = (descripcion || "").replace("{}", `{${respTL.palabrasClave}}`);
            }

            setNuevaPregunta({
                descripcion: descripcionConRespuesta,
                tipo: "textoLibre",
                opciones: [
                    { llave: "A", descripcion: "", orden: 1 },
                    { llave: "B", descripcion: "", orden: 2 },
                ],
                respuestaCorrecta: "",
                longitudMaxima: respTL.longitudMaxima || 255,
                palabraClave: respTL.palabrasClave || "",
                respuestaVerdaderoFalso: null,
            });
        } else if (idTipoPregunta === 3) {
            // Pregunta de verdadero/falso
            const respVF = pregunta.respuestaVerdaderoFalso || {};

            setNuevaPregunta({
                descripcion,
                tipo: "verdaderoFalso",
                opciones: [
                    { llave: "A", descripcion: "", orden: 1 },
                    { llave: "B", descripcion: "", orden: 2 },
                ],
                respuestaCorrecta: "",
                longitudMaxima: 255,
                palabraClave: "",
                respuestaVerdaderoFalso: respVF.respuestaCorrecta !== undefined ? respVF.respuestaCorrecta : null,
            });
        } else {
            // Pregunta de opción múltiple (por defecto)
            const opcionesMapeadas = (pregunta.opciones || []).map((o, idx) => ({
                llave: o.llave || String.fromCharCode(65 + idx),
                descripcion: o.descripcion || o.texto || o.Texto || "",
                orden: o.orden || idx + 1,
                idOpcion: o.idExamenOpcion || o.idOpcion || o.IdExamenOpcion,
            }));

            // Asegurar ghost al final si no existe
            if (opcionesMapeadas.length === 0 || opcionesMapeadas[opcionesMapeadas.length - 1].descripcion.trim() !== '') {
                const letter = String.fromCharCode(65 + opcionesMapeadas.length);
                opcionesMapeadas.push({ llave: letter, descripcion: "", orden: opcionesMapeadas.length + 1 });
            }

            // Detectar si permite múltiples respuestas
            const permiteMultiple = pregunta.permiteMultiple || false;

            // Obtener todas las opciones correctas
            const opcionesCorrectas = (pregunta.opciones || [])
                .filter(o => o.esCorrecta || o.EsCorrecta)
                .map(o => o.llave || "A");

            // Para compatibilidad con modo simple
            const respuestaCorrectaLlave = opcionesCorrectas.length > 0
                ? opcionesCorrectas[0]
                : "";

            setNuevaPregunta({
                descripcion,
                tipo: "opcionMultiple",
                opciones: opcionesMapeadas.length >= 2 ? opcionesMapeadas : [
                    { llave: "A", descripcion: "", orden: 1 },
                    { llave: "B", descripcion: "", orden: 2 },
                ],
                respuestaCorrecta: respuestaCorrectaLlave,
                respuestasCorrectas: opcionesCorrectas,
                permiteMultiple: permiteMultiple,
                longitudMaxima: 255,
                palabraClave: "",
                respuestaVerdaderoFalso: null,
            });
        }

        setModoModal("editar");
        setPreguntaEditandoId(idPregunta);
        setValidationError("");
        setModalNuevaPregunta(true);
    };

    const handleGuardarPreguntaEditada = async () => {
        if (!nuevaPregunta.descripcion.trim()) {
            setAlertDialog({
                open: true,
                title: "Validación",
                message: "Por favor ingresa la descripción de la pregunta.",
                variant: "warning",
            });
            return;
        }

        const preguntaOriginal = preguntas.find(
            (p) => p.idExamenPregunta === preguntaEditandoId
        );

        if (!preguntaOriginal) {
            setAlertDialog({
                open: true,
                title: "Error",
                message: "Error: no se encontró la pregunta original.",
                variant: "error",
            });
            return;
        }

        const idTipoPreguntaOriginal = preguntaOriginal.idTipoPregunta || preguntaOriginal.IdTipoPregunta || 0;
        let descripcionParaGuardar = nuevaPregunta.descripcion;
        let respuestaTextoLibreEditada = "";

        if (idTipoPreguntaOriginal === 1) {
            const openBraces = (nuevaPregunta.descripcion.match(/\{/g) || []).length;
            const closeBraces = (nuevaPregunta.descripcion.match(/\}/g) || []).length;

            if (openBraces !== 1 || closeBraces !== 1) {
                setAlertDialog({
                    open: true,
                    title: "Validación",
                    message: "Formato inválido: Solo se permite un único par de llaves { } para definir la respuesta. Evita llaves dobles {{ }} o múltiples pares.",
                    variant: "warning",
                });
                return;
            }

            const match = nuevaPregunta.descripcion.match(/\{([^}]*)\}/);
            if (!match || !match[1].trim()) {
                setAlertDialog({
                    open: true,
                    title: "Validación",
                    message: "En preguntas abiertas, coloca la respuesta esperada entre llaves, por ejemplo: La capital de Francia es {Paris}.",
                    variant: "warning",
                });
                return;
            }
            respuestaTextoLibreEditada = match[1].trim();
            descripcionParaGuardar = nuevaPregunta.descripcion.replace(/\{[^}]*\}/, "{}");
        }

        if (idTipoPreguntaOriginal === 2) {
            if (nuevaPregunta.permiteMultiple) {
                if (!nuevaPregunta.respuestasCorrectas || nuevaPregunta.respuestasCorrectas.length === 0) {
                    setAlertDialog({
                        open: true,
                        title: "Validación",
                        message: "Por favor selecciona al menos una respuesta correcta.",
                        variant: "warning",
                    });
                    return;
                }
            } else {
                if (!nuevaPregunta.respuestaCorrecta) {
                    setAlertDialog({
                        open: true,
                        title: "Validación",
                        message: "Por favor selecciona la respuesta correcta.",
                        variant: "warning",
                    });
                    return;
                }
            }
            const opcionesValidas = nuevaPregunta.opciones.filter((o) => o.descripcion.trim());
            if (opcionesValidas.length < 2) {
                setAlertDialog({
                    open: true,
                    title: "Validación",
                    message: "Debes agregar al menos 2 opciones.",
                    variant: "warning",
                });
                return;
            }
        }

        // 🚀 OPTIMISTIC UPDATE: Actualizar pregunta inmediatamente en la UI
        const preguntaActualizada = {
            ...preguntaOriginal,
            descripcionPregunta: descripcionParaGuardar,
            permiteMultiple: nuevaPregunta.permiteMultiple,
            opciones: idTipoPreguntaOriginal === 2 ? nuevaPregunta.opciones.filter(o => o.descripcion.trim()).map(o => {
                const esCorrecta = nuevaPregunta.permiteMultiple
                    ? (nuevaPregunta.respuestasCorrectas || []).includes(o.llave)
                    : o.llave === nuevaPregunta.respuestaCorrecta;
                return {
                    ...o,
                    idExamenOpcion: o.idOpcion || o.IdExamenOpcion || o.idExamenOpcion || Date.now() + Math.random(),
                    esCorrecta: esCorrecta,
                    texto: o.descripcion,
                };
            }) : preguntaOriginal.opciones,
            respuestaTextoLibre: idTipoPreguntaOriginal === 1 ? {
                ...preguntaOriginal.respuestaTextoLibre,
                palabrasClave: respuestaTextoLibreEditada,
                longitudMaxima: respuestaTextoLibreEditada.length + 10,
            } : preguntaOriginal.respuestaTextoLibre,
        };

        setPreguntas(prev => prev.map(p => p.idExamenPregunta === preguntaEditandoId ? preguntaActualizada : p));
        setModalNuevaPregunta(false);
        setModoModal("crear");
        setPreguntaEditandoId(null);
        const preguntaLimpia = {
            descripcion: "",
            tipo: "opcionMultiple",
            opciones: [
                { llave: "A", descripcion: "", orden: 1 },
                { llave: "B", descripcion: "", orden: 2 },
            ],
            respuestaCorrecta: "",
            longitudMaxima: 255,
            palabraClave: "",
        };
        setNuevaPregunta(preguntaLimpia);

        // 🔄 Sincronizar con el servidor en segundo plano
        try {

            // 1) Actualizar la pregunta
            const preguntaDTO = {
                IdExamenPregunta: preguntaEditandoId,
                IdExamen: parseInt(examenId, 10),
                DescripcionPregunta: descripcionParaGuardar,
                IdTipoPregunta: idTipoPreguntaOriginal || 2,
                IdRespuestaCorrecta: 0,
                Activo: true,
            };

            await axios.put(
                LMS_ENDPOINTS.examenPreguntas,
                preguntaDTO
            );

            if (idTipoPreguntaOriginal === 2) {
                // Validaciones específicas para opción múltiple
                if (nuevaPregunta.permiteMultiple) {
                    if (!nuevaPregunta.respuestasCorrectas || nuevaPregunta.respuestasCorrectas.length === 0) {
                        setAlertDialog({
                            open: true,
                            title: "Validación",
                            message: "Por favor selecciona al menos una respuesta correcta.",
                            variant: "warning",
                        });
                        return;
                    }
                } else {
                    if (!nuevaPregunta.respuestaCorrecta) {
                        setAlertDialog({
                            open: true,
                            title: "Validación",
                            message: "Por favor selecciona la respuesta correcta.",
                            variant: "warning",
                        });
                        return;
                    }
                }

                const opcionesValidas = nuevaPregunta.opciones.filter((o) =>
                    o.descripcion.trim()
                );
                if (opcionesValidas.length < 2) {
                    setAlertDialog({
                        open: true,
                        title: "Validación",
                        message: "Debes agregar al menos 2 opciones.",
                        variant: "warning",
                    });
                    return;
                }

                // 2) Actualizar la cabecera de la respuesta (PermiteMultiple)
                const idRespuesta = preguntaOriginal.idRespuesta;
                const respuestaDTO = {
                    IdExamenRespuestaOpcionMultiple: idRespuesta,
                    IdExamenPregunta: preguntaEditandoId,
                    PermiteMultiple: nuevaPregunta.permiteMultiple,
                    Activo: true,
                };

                await axios.put(
                    LMS_ENDPOINTS.examenPreguntaOpcionMultiple,
                    respuestaDTO
                );

                // 3) Actualizar opciones
                const opcionesOriginales = preguntaOriginal.opciones || [];

                // Eliminar opciones que ya no existen
                for (const opcionOriginal of opcionesOriginales) {
                    const idOpcion = opcionOriginal.idExamenOpcion || opcionOriginal.idOpcion || opcionOriginal.IdExamenOpcion;
                    const existeEnNuevas = opcionesValidas.some(
                        (o) => (o.idOpcion || o.IdOpcion || o.idExamenOpcion) === idOpcion
                    );

                    if (!existeEnNuevas && idOpcion) {
                        await axios.delete(
                            LMS_ENDPOINTS.examenPreguntaOpcionMultipleOpcionesById(idOpcion)
                        );
                    }
                }

                // Actualizar o crear opciones
                for (const opcion of opcionesValidas) {
                    const esCorrecta = nuevaPregunta.permiteMultiple
                        ? (nuevaPregunta.respuestasCorrectas || []).includes(opcion.llave)
                        : opcion.llave === nuevaPregunta.respuestaCorrecta;
                    const idOpcion = opcion.idOpcion || opcion.IdOpcion || opcion.idExamenOpcion;

                    if (idOpcion) {
                        const opcionDTO = {
                            IdExamenOpcion: idOpcion,
                            IdExamenRespuestaOpcionMultiple: idRespuesta,
                            Texto: opcion.descripcion,
                            EsCorrecta: esCorrecta,
                            Orden: opcion.orden,
                            Activo: true,
                        };

                        await axios.put(
                            LMS_ENDPOINTS.examenPreguntaOpcionMultipleOpciones,
                            opcionDTO
                        );
                    } else {
                        const opcionDTO = {
                            IdExamenOpcion: 0,
                            IdExamenRespuestaOpcionMultiple: idRespuesta,
                            Texto: opcion.descripcion,
                            EsCorrecta: esCorrecta,
                            Orden: opcion.orden,
                            Activo: true,
                        };

                        await axios.post(
                            LMS_ENDPOINTS.examenPreguntaOpcionMultipleOpciones,
                            opcionDTO
                        );
                    }
                }
            } else if (idTipoPreguntaOriginal === 1) {
                // Pregunta de texto libre: actualizar/crear registro en ExamenPreguntaTextoLibre
                const respTL = preguntaOriginal.respuestaTextoLibre || {};

                const respuesta = (respuestaTextoLibreEditada || "").trim();
                const longitud = respuesta.length + 10;

                if (respTL.idExamenRespuestaTextoLibre) {
                    const dto = {
                        IdExamenRespuestaTextoLibre: respTL.idExamenRespuestaTextoLibre,
                        IdExamenPregunta: preguntaEditandoId,
                        LongitudMaxima: longitud,
                        PalabrasClave: respuesta,
                        Activo: true,
                    };

                    await axios.put(
                        LMS_ENDPOINTS.examenPreguntaTextoLibre,
                        dto
                    );
                } else {
                    const dto = {
                        IdExamenRespuestaTextoLibre: 0,
                        IdExamenPregunta: preguntaEditandoId,
                        LongitudMaxima: longitud,
                        PalabrasClave: respuesta,
                        Activo: true,
                    };

                    await axios.post(
                        LMS_ENDPOINTS.examenPreguntaTextoLibre,
                        dto
                    );
                }
            } else if (idTipoPreguntaOriginal === 3) {
                // Pregunta de verdadero/falso: actualizar/crear registro en ExamenPreguntaVerdaderoFalso
                const respVF = preguntaOriginal.respuestaVerdaderoFalso || {};

                if (respVF.idExamenRespuestaVF) {
                    const dto = {
                        IdExamenRespuestaVF: respVF.idExamenRespuestaVF,
                        IdExamenPregunta: preguntaEditandoId,
                        RespuestaCorrecta: nuevaPregunta.respuestaVerdaderoFalso,
                        Activo: true,
                    };

                    await axios.put(
                        LMS_ENDPOINTS.examenPreguntaVerdaderoFalso,
                        dto
                    );
                } else {
                    const dto = {
                        IdExamenRespuestaVF: 0,
                        IdExamenPregunta: preguntaEditandoId,
                        RespuestaCorrecta: nuevaPregunta.respuestaVerdaderoFalso,
                        Activo: true,
                    };

                    await axios.post(
                        LMS_ENDPOINTS.examenPreguntaVerdaderoFalso,
                        dto
                    );
                }
            }

            console.log("✅ Pregunta actualizada y sincronizada con el servidor");
            await loadExamen(false);
        } catch (error) {
            console.error("❌ Error al actualizar pregunta:", error);
            setAlertDialog({
                open: true,
                title: "Error",
                message: "Error al actualizar la pregunta. Se revertirán los cambios.",
                variant: "error",
            });
            setPreguntas(prev => prev.map(p => p.idExamenPregunta === preguntaEditandoId ? preguntaOriginal : p));
            setNuevaPregunta({
                descripcion: nuevaPregunta.descripcion,
                tipo: nuevaPregunta.tipo,
                opciones: nuevaPregunta.opciones,
                respuestaCorrecta: nuevaPregunta.respuestaCorrecta,
                longitudMaxima: nuevaPregunta.longitudMaxima,
                palabraClave: respuestaTextoLibreEditada,
            });
            setModoModal("editar");
            setPreguntaEditandoId(preguntaEditandoId);
            setModalNuevaPregunta(true);
        }
    };

    // ----------- Guardar configuración del examen -----------
    const handleGuardarConfiguracion = async () => {
        if (!examen) return;

        // 🚀 OPTIMISTIC UPDATE: Actualizar examen inmediatamente
        const examenAnterior = { ...examen };
        const examenActualizado = {
            ...examen,
            preguntasAleatorias: preguntasAleatorias,
            limiteIntentos: limiteIntentos,
        };
        setExamen(examenActualizado);

        // 🔄 Sincronizar con el servidor en segundo plano
        try {
            const examenDTO = {
                IdExamen: examen.idExamen,
                IdCurso: examen.idCurso,
                Descripcion: examen.descripcion,
                Nombre: examen.nombre,
                PreguntasAleatorias: preguntasAleatorias,
                LimiteIntentos: limiteIntentos,
                Activo: examen.activo
            };

            await axios.put(LMS_ENDPOINTS.examen, examenDTO);

            console.log("✅ Configuración guardada y sincronizada con el servidor");
            await loadExamen(false);
        } catch (error) {
            console.error("❌ Error al guardar configuración:", error);
            setAlertDialog({
                open: true,
                title: "Error",
                message: "Error al guardar la configuración. Se revertirán los cambios.",
                variant: "error",
            });
            setExamen(examenAnterior);
            setPreguntasAleatorias(examenAnterior.preguntasAleatorias || 0);
            setLimiteIntentos(examenAnterior.limiteIntentos || 3);
        }
    };

    // ----------- Render -----------
    if (loading) {
        return (
            <div className="cc-page">
                <div className="cc-loading">Cargando examen...</div>
            </div>
        );
    }

    if (!examen) {
        return (
            <div className="cc-page">
                <div className="cc-error">Examen no encontrado</div>
            </div>
        );
    }

    return (
        <div className="cc-page">
            {/* Breadcrumb */}
            <div className="cc-breadcrumb">
                <button
                    type="button"
                    className="cc-breadcrumb-back"
                    onClick={() => navigate(-1)}
                >
                    <FiArrowLeft />
                    Volver
                </button>
            </div>



            {/* Panel de Configuración */}
            <div className="quiz-config-panel">
                <h3 className="quiz-config-title">⚙️ Configuración del Examen</h3>

                <div className="quiz-config-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                    {/* Nombre del Examen */}
                    <div className="quiz-config-item" style={{ gridColumn: '1 / -1' }}>
                        <label className="quiz-config-label" htmlFor="nombreExamen">
                            Nombre del Examen
                        </label>
                        <input
                            id="nombreExamen"
                            type="text"
                            className="quiz-config-input"
                            value={examen.nombre || examen.Nombre || ""}
                            onChange={(e) =>
                                setExamen({
                                    ...examen,
                                    nombre: e.target.value,
                                    Nombre: e.target.value,
                                })
                            }
                            placeholder="Escribe el nombre del examen"
                        />
                    </div>

                    {/* Total de preguntas */}
                    <div className="quiz-config-item">
                        <label className="quiz-config-label">Total de Preguntas</label>
                        <div className="quiz-config-value-display">
                            {preguntas.length} {preguntas.length === 1 ? 'pregunta' : 'preguntas'}
                        </div>
                    </div>

                    {/* Preguntas aleatorias */}
                    <div className="quiz-config-item">
                        <label className="quiz-config-label" htmlFor="preguntasAleatorias">
                            Preguntas Aleatorias
                        </label>
                        <input
                            id="preguntasAleatorias"
                            type="number"
                            className="quiz-config-input"
                            min="0"
                            max={preguntas.length}
                            value={preguntasAleatorias}
                            onChange={(e) => {
                                const val = parseInt(e.target.value, 10);
                                if (val >= 0 && val <= preguntas.length) {
                                    setPreguntasAleatorias(val);
                                }
                            }}
                        />
                        <small className="quiz-config-hint">
                            {preguntasAleatorias === 0
                                ? 'Se mostrarán todas las preguntas'
                                : `Se mostrarán ${preguntasAleatorias} de ${preguntas.length} preguntas al azar`}
                        </small>
                    </div>

                    {/* Límite de intentos */}
                    <div className="quiz-config-item">
                        <label className="quiz-config-label" htmlFor="limiteIntentos">
                            Límite de Intentos
                        </label>
                        <input
                            id="limiteIntentos"
                            type="number"
                            className="quiz-config-input"
                            min="1"
                            max="5"
                            value={limiteIntentos}
                            onChange={(e) => {
                                const val = parseInt(e.target.value, 10);
                                if (val >= 1 && val <= 5) {
                                    setLimiteIntentos(val);
                                }
                            }}
                        />
                        <small className="quiz-config-hint">
                            Los estudiantes podrán intentar {limiteIntentos} {limiteIntentos === 1 ? 'vez' : 'veces'}
                        </small>
                    </div>
                </div>

                <div className="quiz-config-actions">
                    <button
                        type="button"
                        className="quiz-config-save-btn"
                        onClick={handleGuardarConfiguracion}
                    >
                        <FiSave /> Guardar Configuración
                    </button>
                </div>
            </div>

            {/* Lista de preguntas */}
            <div className="cc-questions-list">
                {preguntas.length === 0 ? (
                    <div className="cc-empty-state">
                        <p>
                            No hay preguntas aún. Haz clic en "Agregar Pregunta" para
                            comenzar.
                        </p>
                    </div>
                ) : (
                    preguntas.map((pregunta, index) => {
                        const descripcionBase =
                            pregunta.descripcionPregunta ||
                            pregunta.DescripcionPregunta ||
                            "";

                        let descripcion = descripcionBase;
                        const idTipoPregunta = pregunta.idTipoPregunta || pregunta.IdTipoPregunta || 0;
                        if (idTipoPregunta === 1 && pregunta.respuestaTextoLibre && pregunta.respuestaTextoLibre.palabrasClave) {
                            descripcion = (descripcionBase || "").replace("{}", `{${pregunta.respuestaTextoLibre.palabrasClave}}`);
                        }

                        const keyPregunta = pregunta.idExamenPregunta;

                        return (
                            <div key={keyPregunta} className="cc-question-card">
                                <div className="cc-question-header">
                                    <h3 className="cc-question-number">
                                        Pregunta {index + 1}
                                    </h3>
                                    <div className="cc-question-actions">
                                        <button
                                            type="button"
                                            className="cc-icon-btn cc-icon-btn-edit"
                                            onClick={() => handleEditarPregunta(pregunta)}
                                            title="Editar pregunta"
                                        >
                                            <FiEdit2 />
                                        </button>
                                        <button
                                            type="button"
                                            className="cc-icon-btn cc-icon-btn-danger"
                                            onClick={() => handleEliminarPregunta(pregunta)}
                                            title="Eliminar pregunta"
                                        >
                                            <FiTrash2 />
                                        </button>
                                    </div>
                                </div>

                                <p className="cc-question-text">{descripcion}</p>

                                {idTipoPregunta === 2 && (
                                    <div className="cc-options-list">
                                        {(pregunta.opciones || []).map((opcion, idx) => {
                                            const llave =
                                                opcion.llave ||
                                                opcion.Llave ||
                                                String.fromCharCode(65 + idx);
                                            const desc =
                                                opcion.descripcion ||
                                                opcion.Descripcion ||
                                                opcion.texto ||
                                                opcion.Texto ||
                                                "";
                                            const esCorrecta =
                                                opcion.esCorrecta !== undefined
                                                    ? opcion.esCorrecta
                                                    : opcion.EsCorrecta !== undefined
                                                        ? opcion.EsCorrecta
                                                        : false;

                                            const onOptionDragStart = (e) => {
                                                e.dataTransfer.setData("application/json", JSON.stringify({ qIdx: index, oIdx: idx }));
                                                e.dataTransfer.effectAllowed = "move";
                                            };

                                            const onOptionDragOver = (e) => {
                                                e.preventDefault();
                                            };

                                            const onOptionDrop = (e) => {
                                                e.preventDefault();
                                                const data = JSON.parse(e.dataTransfer.getData("application/json"));
                                                if (data.qIdx !== index || data.oIdx === idx) return;

                                                const nuevasPreguntas = [...preguntas];
                                                const preguntaActual = nuevasPreguntas[index];
                                                const opciones = [...preguntaActual.opciones];

                                                const objetosIndicesCorrectos = new Set();
                                                opciones.forEach((op) => {
                                                    const esOpCorrecta = preguntaActual.permiteMultiple
                                                        ? (preguntaActual.respuestasCorrectas || []).includes(op.llave)
                                                        : preguntaActual.respuestaCorrecta === op.llave;
                                                    if (esOpCorrecta) objetosIndicesCorrectos.add(op);
                                                });

                                                const [moved] = opciones.splice(data.oIdx, 1);
                                                opciones.splice(idx, 0, moved);

                                                const opcionesRenombradas = opciones.map((op, i) => ({
                                                    ...op,
                                                    llave: String.fromCharCode(65 + i),
                                                    orden: i + 1
                                                }));

                                                preguntaActual.opciones = opcionesRenombradas;

                                                if (preguntaActual.permiteMultiple) {
                                                    preguntaActual.respuestasCorrectas = opcionesRenombradas
                                                        .filter(op => objetosIndicesCorrectos.has(op))
                                                        .map(op => op.llave);
                                                } else {
                                                    const indicesDeCorrectasEnNuevoOrden = [];
                                                    opciones.forEach((op, i) => {
                                                        if (objetosIndicesCorrectos.has(op)) indicesDeCorrectasEnNuevoOrden.push(i);
                                                    });

                                                    if (indicesDeCorrectasEnNuevoOrden.length > 0) {
                                                        preguntaActual.respuestaCorrecta = String.fromCharCode(65 + indicesDeCorrectasEnNuevoOrden[0]);
                                                    } else {
                                                        preguntaActual.respuestaCorrecta = "";
                                                    }
                                                }

                                                setPreguntas(nuevasPreguntas);
                                            };

                                            return (
                                                <div
                                                    key={
                                                        opcion.idExamenOpcion ||
                                                        opcion.idOpcion ||
                                                        opcion.IdExamenOpcion ||
                                                        idx
                                                    }
                                                    className={`cc-option-item ${esCorrecta ? "cc-option-correct" : ""}`}
                                                    onClick={() => handleCambiarRespuestaCorrecta(pregunta, opcion)}
                                                    draggable
                                                    onDragStart={onOptionDragStart}
                                                    onDragOver={onOptionDragOver}
                                                    onDrop={onOptionDrop}
                                                    style={{ cursor: 'move' }}
                                                    title={esCorrecta ? "Esta es la respuesta correcta" : "Clic para marcar como correcta"}
                                                >
                                                    <span className="cc-option-letter">{llave}</span>
                                                    <span className="cc-option-text">{desc}</span>
                                                    {esCorrecta && (
                                                        <span className="cc-correct-badge">✓ Correcta</span>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* Para preguntas de verdadero/falso mostramos botones con estilo de opción correcta */}
                                {idTipoPregunta === 3 && pregunta.respuestaVerdaderoFalso && (
                                    <div style={{ marginTop: '1rem' }}>
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <div
                                                onClick={() => handleCambiarRespuestaVerdaderoFalso(pregunta, true)}
                                                className={`cc-option-item ${pregunta.respuestaVerdaderoFalso.respuestaCorrecta === true ? 'cc-option-correct' : ''}`}
                                                style={{ cursor: 'pointer', flex: 1 }}
                                                title={pregunta.respuestaVerdaderoFalso.respuestaCorrecta === true ? "Esta es la respuesta correcta" : "Clic para marcar como correcta"}
                                            >
                                                <span className="cc-option-letter">✓</span>
                                                <span className="cc-option-text">Verdadero</span>
                                                {pregunta.respuestaVerdaderoFalso.respuestaCorrecta === true && (
                                                    <span className="cc-correct-badge">Correcta</span>
                                                )}
                                            </div>
                                            <div
                                                onClick={() => handleCambiarRespuestaVerdaderoFalso(pregunta, false)}
                                                className={`cc-option-item ${pregunta.respuestaVerdaderoFalso.respuestaCorrecta === false ? 'cc-option-correct' : ''}`}
                                                style={{ cursor: 'pointer', flex: 1 }}
                                                title={pregunta.respuestaVerdaderoFalso.respuestaCorrecta === false ? "Esta es la respuesta correcta" : "Clic para marcar como correcta"}
                                            >
                                                <span className="cc-option-letter">✗</span>
                                                <span className="cc-option-text">Falso</span>
                                                {pregunta.respuestaVerdaderoFalso.respuestaCorrecta === false && (
                                                    <span className="cc-correct-badge">Correcta</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            {/* Botón agregar pregunta */}
            <div className="cc-toolbar" style={{ marginTop: '1.5rem' }}>
                <button
                    type="button"
                    className="cc-btn-primary"
                    onClick={handleAbrirModalNuevaPregunta}
                >
                    <FiPlus /> Agregar Pregunta
                </button>
            </div>

            {/* Modal Nueva Pregunta */}
            {modalNuevaPregunta && (
                <div className="cc-modal-overlay">
                    <div className="cc-modal cc-modal-large">
                        <div className="cc-modal-header">
                            <h3>{modoModal === "editar" ? "Editar Pregunta" : "Agregar Nueva Pregunta"}</h3>
                            <button
                                type="button"
                                className="cc-modal-close"
                                onClick={() => setModalNuevaPregunta(false)}
                            >
                                <FiX />
                            </button>
                        </div>

                        <div className="cc-modal-body">
                            {/* Descripción de la pregunta */}
                            <div className="cc-form-group">
                                <label>Pregunta</label>
                                <textarea
                                    className="cc-textarea"
                                    rows={3}
                                    value={nuevaPregunta.descripcion}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setNuevaPregunta({
                                            ...nuevaPregunta,
                                            descripcion: val,
                                        });

                                        if (nuevaPregunta.tipo === "textoLibre") {
                                            const openBraces = (val.match(/\{/g) || []).length;
                                            const closeBraces = (val.match(/\}/g) || []).length;
                                            let error = "";

                                            if (openBraces > 1 || closeBraces > 1) {
                                                error = "Formato inválido: Solo se permite un único par de llaves { } por pregunta.";
                                            } else if (openBraces === 1 && closeBraces === 1) {
                                                if (val.indexOf('}') < val.indexOf('{')) {
                                                    error = "Formato inválido: La llave de cierre } está antes de la llave de apertura {.";
                                                } else {
                                                    const content = val.slice(val.indexOf('{') + 1, val.indexOf('}')).trim();
                                                    if (!content) {
                                                        error = "El contenido entre llaves no puede estar vacío.";
                                                    }
                                                }
                                            }
                                            setValidationError(error);
                                        } else {
                                            setValidationError("");
                                        }
                                    }}
                                    placeholder="Escribe la pregunta aquí..."
                                />
                                {nuevaPregunta.tipo === "textoLibre" && (
                                    <>
                                        <small className="quiz-config-hint">
                                            En preguntas abiertas, coloca la respuesta esperada entre llaves, por ejemplo: "La capital de Francia es {'{Paris}'}".
                                        </small>
                                        {validationError && (
                                            <div style={{ color: '#ef4444', fontSize: '0.85rem', marginTop: '4px', fontWeight: 500 }}>
                                                {validationError}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* Tipo de pregunta - Solo visible al crear */}
                            {modoModal === "crear" && (
                                <div className="cc-form-group">
                                    <label>Tipo de Pregunta</label>

                                    <div style={{
                                        display: 'flex',
                                        backgroundColor: '#f1f5f9',
                                        borderRadius: '12px',
                                        padding: '4px',
                                        border: '1px solid #e2e8f0',
                                        height: 'fit-content',
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                        width: '100%',
                                        position: 'relative'
                                    }}>
                                        {/* Sliding Background */}
                                        <div style={{
                                            position: 'absolute',
                                            top: '4px',
                                            bottom: '4px',
                                            left: '4px',
                                            width: 'calc((100% - 8px) / 3)',
                                            backgroundColor: '#ffffff',
                                            borderRadius: '8px',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                            transform: nuevaPregunta.tipo === 'opcionMultiple' ? 'translateX(0)' :
                                                nuevaPregunta.tipo === 'verdaderoFalso' ? 'translateX(100%)' : 'translateX(200%)',
                                            transition: 'transform 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)'
                                        }} />

                                        {/* Option: Opción Múltiple */}
                                        <div
                                            onClick={() => setNuevaPregunta({ ...nuevaPregunta, tipo: "opcionMultiple" })}
                                            style={{
                                                zIndex: 1,
                                                flex: 1,
                                                textAlign: 'center',
                                                padding: '8px 0',
                                                fontSize: '0.9rem',
                                                fontWeight: nuevaPregunta.tipo === 'opcionMultiple' ? 600 : 500,
                                                color: nuevaPregunta.tipo === 'opcionMultiple' ? 'var(--color-primary)' : '#64748b',
                                                transition: 'color 0.2s',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '0.5rem'
                                            }}
                                        >
                                            Opción múltiple
                                        </div>

                                        {/* Option: Verdadero o Falso */}
                                        <div
                                            onClick={() => setNuevaPregunta({ ...nuevaPregunta, tipo: "verdaderoFalso" })}
                                            style={{
                                                zIndex: 1,
                                                flex: 1,
                                                textAlign: 'center',
                                                padding: '8px 0',
                                                fontSize: '0.9rem',
                                                fontWeight: nuevaPregunta.tipo === 'verdaderoFalso' ? 600 : 500,
                                                color: nuevaPregunta.tipo === 'verdaderoFalso' ? 'var(--color-primary)' : '#64748b',
                                                transition: 'color 0.2s',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '0.5rem'
                                            }}
                                        >
                                            Verdadero o Falso
                                        </div>

                                        {/* Option: Pregunta abierta */}
                                        <div
                                            onClick={() => setNuevaPregunta({
                                                ...nuevaPregunta,
                                                tipo: "textoLibre",
                                                respuestaCorrecta: "",
                                            })}
                                            style={{
                                                zIndex: 1,
                                                flex: 1,
                                                textAlign: 'center',
                                                padding: '8px 0',
                                                fontSize: '0.9rem',
                                                fontWeight: nuevaPregunta.tipo === 'textoLibre' ? 600 : 500,
                                                color: nuevaPregunta.tipo === 'textoLibre' ? 'var(--color-primary)' : '#64748b',
                                                transition: 'color 0.2s',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '0.5rem'
                                            }}
                                        >
                                            Pregunta abierta
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Opciones (solo para opción múltiple) */}
                            {nuevaPregunta.tipo === "opcionMultiple" && (
                                <div className="cc-form-group">
                                    <label>Opciones de Respuesta</label>

                                    {/* Toggle Tipo de Respuesta: Una vs Múltiples (Estilo Pill Animado) */}
                                    <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151' }}>Tipo de selección</label>

                                        <div
                                            onClick={() => {
                                                const nuevoValor = !nuevaPregunta.permiteMultiple;
                                                const primeraCorrecta = nuevaPregunta.respuestasCorrectas && nuevaPregunta.respuestasCorrectas.length > 0
                                                    ? nuevaPregunta.respuestasCorrectas[0]
                                                    : '';

                                                setNuevaPregunta({
                                                    ...nuevaPregunta,
                                                    permiteMultiple: nuevoValor,
                                                    // Si pasamos a single (false), seteamos la primera correcta si existe
                                                    // Si pasamos a multiple (true), inicializamos el array con la correcta actual si existe
                                                    respuestaCorrecta: !nuevoValor ? primeraCorrecta : nuevaPregunta.respuestaCorrecta,
                                                    respuestasCorrectas: nuevoValor ? (nuevaPregunta.respuestaCorrecta ? [nuevaPregunta.respuestaCorrecta] : []) : (primeraCorrecta ? [primeraCorrecta] : [])
                                                });
                                            }}
                                            style={{
                                                position: 'relative',
                                                display: 'flex',
                                                backgroundColor: '#f1f5f9',
                                                borderRadius: '999px',
                                                padding: '4px',
                                                border: '1px solid #e2e8f0',
                                                height: 'fit-content',
                                                cursor: 'pointer',
                                                userSelect: 'none',
                                                width: '300px'
                                            }}
                                        >
                                            {/* Sliding Background */}
                                            <div style={{
                                                position: 'absolute',
                                                top: '4px',
                                                bottom: '4px',
                                                left: '4px',
                                                width: 'calc(50% - 4px)',
                                                backgroundColor: '#ffffff',
                                                borderRadius: '999px',
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                                transform: nuevaPregunta.permiteMultiple ? 'translateX(100%)' : 'translateX(0)',
                                                transition: 'transform 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)'
                                            }} />

                                            {/* Labels */}
                                            <div style={{
                                                zIndex: 1,
                                                flex: 1,
                                                textAlign: 'center',
                                                padding: '6px 0',
                                                fontSize: '0.85rem',
                                                fontWeight: !nuevaPregunta.permiteMultiple ? 600 : 500,
                                                color: !nuevaPregunta.permiteMultiple ? 'var(--color-primary)' : '#64748b',
                                                transition: 'color 0.2s'
                                            }}>
                                                Una respuesta
                                            </div>
                                            <div style={{
                                                zIndex: 1,
                                                flex: 1,
                                                textAlign: 'center',
                                                padding: '6px 0',
                                                fontSize: '0.85rem',
                                                fontWeight: nuevaPregunta.permiteMultiple ? 600 : 500,
                                                color: nuevaPregunta.permiteMultiple ? 'var(--color-primary)' : '#64748b',
                                                transition: 'color 0.2s'
                                            }}>
                                                Múltiples respuestas
                                            </div>
                                        </div>
                                    </div>
                                    <div className="cc-options-wrapper">
                                        {nuevaPregunta.opciones.map((opcion, index) => {
                                            const isGhost = index === nuevaPregunta.opciones.length - 1;



                                            return (
                                                <div
                                                    key={index}
                                                    className="cc-option-input-row"
                                                >
                                                    <span className="cc-option-label">{opcion.llave}</span>
                                                    <input
                                                        type="text"
                                                        className={`cc-input ${index === nuevaPregunta.opciones.length - 1 ? 'cc-ghost-option' : ''}`}
                                                        value={opcion.descripcion}
                                                        onChange={(e) => {
                                                            const val = e.target.value;
                                                            let nuevasOpciones = [...nuevaPregunta.opciones];
                                                            nuevasOpciones[index].descripcion = val;

                                                            // Lógica de crecimiento/decrecimiento dinámico
                                                            const totalOpciones = nuevasOpciones.length;
                                                            const esUltima = index === totalOpciones - 1;
                                                            const esPenultima = index === totalOpciones - 2;

                                                            // Lógica de crecimiento (Auto-grow)
                                                            if (esUltima && val.trim() !== "" && totalOpciones < 10) {
                                                                const nextLetter = String.fromCharCode(65 + totalOpciones);
                                                                nuevasOpciones.push({
                                                                    llave: nextLetter,
                                                                    descripcion: "",
                                                                    orden: totalOpciones + 1
                                                                });
                                                            }

                                                            // Lógica de decrecimiento en cascada (Shrink)
                                                            // Si borramos texto, verificamos si sobran opciones vacías al final
                                                            else if (val.trim() === "") {
                                                                // Encontrar el último índice con texto real
                                                                let lastNonEmptyIndex = -1;
                                                                for (let i = nuevasOpciones.length - 1; i >= 0; i--) {
                                                                    if (nuevasOpciones[i].descripcion.trim() !== "") {
                                                                        lastNonEmptyIndex = i;
                                                                        break;
                                                                    }
                                                                }

                                                                // Queremos mantener hasta (lastNonEmptyIndex + 1 para el ghost), 
                                                                // pero respetando el mínimo de 3 opciones (índices 0, 1, 2)
                                                                const targetLength = Math.max(3, lastNonEmptyIndex + 2);

                                                                // Si tenemos más opciones de las necesarias (colas vacías extra), cortamos
                                                                if (nuevasOpciones.length > targetLength) {
                                                                    nuevasOpciones = nuevasOpciones.slice(0, targetLength);
                                                                }
                                                            }

                                                            // Limpieza de respuestas seleccionadas:
                                                            // 1. Eliminar respuestas que ya no existen (por el slice)
                                                            // 2. Deseleccionar la opción fantasma (la última), ya que no es una respuesta válida
                                                            const llavesExistentes = nuevasOpciones.map(o => o.llave);
                                                            const llaveFantasma = nuevasOpciones[nuevasOpciones.length - 1].llave;

                                                            let nuevaRespuestaCorrecta = nuevaPregunta.respuestaCorrecta;
                                                            let nuevasRespuestasCorrectas = nuevaPregunta.respuestasCorrectas || [];

                                                            if (nuevaPregunta.permiteMultiple) {
                                                                // Filtrar: debe existir Y no ser fantasma
                                                                nuevasRespuestasCorrectas = nuevasRespuestasCorrectas.filter(
                                                                    k => llavesExistentes.includes(k) && k !== llaveFantasma
                                                                );
                                                            } else {
                                                                // Si la seleccionada no existe o es la fantasma, limpiar
                                                                if (!llavesExistentes.includes(nuevaRespuestaCorrecta) || nuevaRespuestaCorrecta === llaveFantasma) {
                                                                    nuevaRespuestaCorrecta = "";
                                                                }
                                                            }

                                                            setNuevaPregunta({
                                                                ...nuevaPregunta,
                                                                opciones: nuevasOpciones,
                                                                respuestaCorrecta: nuevaRespuestaCorrecta,
                                                                respuestasCorrectas: nuevasRespuestasCorrectas
                                                            });
                                                        }}
                                                        placeholder={index === nuevaPregunta.opciones.length - 1 ? "Escribe para agregar opción..." : `Opción ${opcion.llave}`}
                                                    />
                                                    {index < nuevaPregunta.opciones.length - 1 ? (
                                                        <input
                                                            type={nuevaPregunta.permiteMultiple ? "checkbox" : "radio"}
                                                            name={nuevaPregunta.permiteMultiple ? undefined : "respuestaCorrecta"}
                                                            style={{ width: '20px', height: '20px', cursor: 'pointer', accentColor: 'var(--color-primary)' }}
                                                            checked={
                                                                nuevaPregunta.permiteMultiple
                                                                    ? (nuevaPregunta.respuestasCorrectas || []).includes(opcion.llave)
                                                                    : nuevaPregunta.respuestaCorrecta === opcion.llave
                                                            }
                                                            onChange={() => {
                                                                if (nuevaPregunta.permiteMultiple) {
                                                                    const correctas = nuevaPregunta.respuestasCorrectas || [];
                                                                    const nuevasCorrectas = correctas.includes(opcion.llave)
                                                                        ? correctas.filter(l => l !== opcion.llave)
                                                                        : [...correctas, opcion.llave];
                                                                    setNuevaPregunta({
                                                                        ...nuevaPregunta,
                                                                        respuestasCorrectas: nuevasCorrectas
                                                                    });
                                                                } else {
                                                                    setNuevaPregunta({
                                                                        ...nuevaPregunta,
                                                                        respuestaCorrecta: opcion.llave,
                                                                    });
                                                                }
                                                            }}
                                                            title={nuevaPregunta.permiteMultiple ? "Marcar/desmarcar como correcta" : "Marcar como correcta"}
                                                        />
                                                    ) : (
                                                        <input
                                                            type={nuevaPregunta.permiteMultiple ? "checkbox" : "radio"}
                                                            style={{ width: '20px', height: '20px', visibility: 'hidden' }}
                                                            checked={false}
                                                            readOnly
                                                            disabled
                                                        />
                                                    )}
                                                    {index < nuevaPregunta.opciones.length - 1 && nuevaPregunta.opciones.length > 3 && (
                                                        <button
                                                            type="button"
                                                            className="cc-option-delete-btn"
                                                            onClick={() => handleEliminarOpcion(index)}
                                                            title="Eliminar opción"
                                                        >
                                                            <FiX />
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>


                                </div>
                            )}

                            {/* Opciones para preguntas de verdadero/falso */}
                            {nuevaPregunta.tipo === "verdaderoFalso" && (
                                <div className="cc-form-group">
                                    <label>Respuesta Correcta</label>
                                    <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
                                        <label style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '8px', transition: 'all 0.2s' }}>
                                            <input
                                                type="radio"
                                                name="respuestaVerdaderoFalsoExamen"
                                                checked={nuevaPregunta.respuestaVerdaderoFalso === true}
                                                onChange={() =>
                                                    setNuevaPregunta({
                                                        ...nuevaPregunta,
                                                        respuestaVerdaderoFalso: true,
                                                    })
                                                }
                                                style={{ width: '20px', height: '20px', cursor: 'pointer', accentColor: 'var(--color-primary)' }}
                                            />
                                            <span style={{ fontWeight: '600', fontSize: '14px' }}>✓ Verdadero</span>
                                        </label>
                                        <label style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '8px', transition: 'all 0.2s' }}>
                                            <input
                                                type="radio"
                                                name="respuestaVerdaderoFalsoExamen"
                                                checked={nuevaPregunta.respuestaVerdaderoFalso === false}
                                                onChange={() =>
                                                    setNuevaPregunta({
                                                        ...nuevaPregunta,
                                                        respuestaVerdaderoFalso: false,
                                                    })
                                                }
                                                style={{ width: '20px', height: '20px', cursor: 'pointer', accentColor: 'var(--color-primary)' }}
                                            />
                                            <span style={{ fontWeight: '600', fontSize: '14px' }}>✗ Falso</span>
                                        </label>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="cc-modal-footer">
                            <button
                                type="button"
                                className="btn-cancel"
                                onClick={() => setModalNuevaPregunta(false)}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="cc-btn-primary"
                                onClick={modoModal === "editar" ? handleGuardarPreguntaEditada : handleAgregarPregunta}
                            >
                                <FiSave /> {modoModal === "editar" ? "Actualizar Pregunta" : "Guardar Pregunta"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Botón FAB para Observaciones */}
            <button
                className="fab-review"
                onClick={() => setIsObservacionesModalOpen(true)}
                title="Ver observaciones"
            >
                <FiMessageSquare />
            </button>

            {/* Modal de Observaciones */}
            <ObservacionesModal
                isOpen={isObservacionesModalOpen}
                onClose={() => setIsObservacionesModalOpen(false)}
                idCurso={idCurso || (examen ? examen.idCurso : null)}
                currentContentId={examenId}
                currentContentType="EXAMEN"
                onObservationAdded={() => { }}
                readOnly={true}
            />

            {/* Modal de alertas estándar LMS */}
            <AlertDialog
                open={alertDialog.open}
                title={alertDialog.title}
                message={alertDialog.message}
                variant={alertDialog.variant}
                onClose={() => {
                    if (alertDialog.onConfirm) {
                        alertDialog.onConfirm();
                    }
                    setAlertDialog({ ...alertDialog, open: false });
                }}
                onCancel={alertDialog.onCancel ? (() => {
                    alertDialog.onCancel();
                    setAlertDialog({ ...alertDialog, open: false });
                }) : undefined}
                buttonText={alertDialog.onConfirm ? "Confirmar" : "Aceptar"}
                cancelText="Cancelar"
            />
        </div>
    );
};

export default Editarexamen;
